import { useEffect, useRef } from "react";
import {
  HMSNotificationTypes,
  useHMSNotifications,
  useHMSActions,
} from "@100mslive/react-sdk";
import toast from "react-hot-toast";
import { globalStores } from "../api/globalStores";
import { resumeSharedAudioContext, unlockAudio } from "../util/sharedAudioContext";

const logger = console;

const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (
    navigator.userAgent.includes("Mac") && "ontouchend" in document
  );
};

export function useHMSExceptionHandler() {
  const notification = useHMSNotifications();
  const lastNotificationIdRef = useRef<string>("");
  const hmsActions = useHMSActions();

  // --- Visibility change handler ---
  // iOS suspends WebRTC when the page goes to background (e.g. student
  // switches to another app briefly).  When it comes back we need to
  // re-unlock audio and nudge HMS to reconnect if needed.
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;

      logger.log("[Visibility] Page returned to foreground");

      // Re-unlock the shared AudioContext (iOS suspends it on background)
      if (isIOS()) {
        await unlockAudio();
        hmsActions.unblockAudio();
        logger.log("[Visibility] Audio re-unlocked after foreground");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [hmsActions]);

  // --- Notification handler ---
  useEffect(() => {
    if (!notification) {
      return;
    }

    const notificationId = String(notification.id || `${notification.type}_${JSON.stringify(notification.data)}`);
    if (notificationId === lastNotificationIdRef.current) {
      return;
    }
    lastNotificationIdRef.current = notificationId;

    const actions = globalStores?.curTalkingParty?.hmsActions as any;

    const safeReconnect = async () => {
      try {
        if (actions && typeof actions.reconnect === "function") {
          await actions.reconnect();
          toast.success("Reconnecting…", { duration: 1500 });
        }
      } catch (error) {
        logger.error("Reconnect attempt failed", error);
      }
    };

    const retryMicIfLive = async () => {
      try {
        if (
          !globalStores?.curTalkingParty?.microphoneOn ||
          !actions ||
          typeof actions.setLocalAudioEnabled !== "function"
        ) {
          return;
        }
        await actions.setLocalAudioEnabled(false);
        setTimeout(() => actions.setLocalAudioEnabled(true), 300);
        toast.success("Audio refreshed", { duration: 1500 });
      } catch (error) {
        logger.error("Mic refresh failed", error);
      }
    };

    logger.log("notification type", notification.type);

    switch (notification.type) {
      case HMSNotificationTypes.PEER_JOINED:
        logger.log(
          `${notification.data.name} of role ${notification.data.roleName} joined`
        );
        break;
      case HMSNotificationTypes.PEER_LEFT:
        logger.log(`${notification.data.name} left`);
        break;
      case HMSNotificationTypes.NEW_MESSAGE:
        logger.log(
          `${notification.data.message} received from ${notification.data.senderName}`
        );
        break;
      case HMSNotificationTypes.ERROR: {
        const code = notification.data?.code;
        const message = notification.data?.message || "";

        if (
          message === "User denied permission to access capture device - audio"
        ) {
          toast.error(
            "Unable to unmute audio: you have denied permission to microphone. Please enable it."
          );
          break;
        }

        if (
          message === "User denied permission to access capture device - video"
        ) {
          toast.error(
            "Unable to unmute video: you have denied permission to your camera. Please enable it."
          );
          break;
        }

        if (
          message === "User denied permission to access capture device - screen"
        ) {
          toast.error(
            "Unable to share screen: you have denied permission to share your screen. Please enable it."
          );
          break;
        }

        // Network / ICE disconnect
        if (
          code === 4006 ||
          code === 1003 ||
          message.toLowerCase().includes("ice connection state disconnected") ||
          message.toLowerCase().includes("websocket connection lost") ||
          message.toLowerCase().includes("ping pong failure")
        ) {
          toast.error("Connection dropped. Trying to reconnect…");
          safeReconnect();
          break;
        }

        // Mic track ended / no data
        if (
          code === 3015 ||
          message.toLowerCase().includes("track has no data")
        ) {
          toast.error(
            "Mic stopped sending audio. Refreshing microphone automatically."
          );
          retryMicIfLive();
          break;
        }

        // Audio playback error – unblock audio via user-gesture path and retry
        if (
          code === 3013 ||
          message.toLowerCase().includes("audio playback error")
        ) {
          toast.error(
            "Audio glitch detected. Attempting automatic recovery…"
          );
          (async () => {
            try {
              await resumeSharedAudioContext();
              hmsActions.unblockAudio();
            } catch (err) {
              logger.error("3013 recovery failed:", err);
            }
          })();
          break;
        }

        toast.error(notification.data.action, { duration: 100 });
        logger.log("[Error]", notification.data);
        logger.log("[Error Code]", notification.data.code);
        break;
      }
      case HMSNotificationTypes.RECONNECTING:
        logger.log("[Reconnecting]", notification.data);
        break;
      case HMSNotificationTypes.RECONNECTED:
        logger.log("[Reconnected]");
        // After reconnection on iOS, re-unlock audio as the context
        // may have been suspended during the disconnected period.
        if (isIOS()) {
          unlockAudio();
          hmsActions.unblockAudio();
        }
        break;
      case HMSNotificationTypes.NAME_UPDATED:
      case HMSNotificationTypes.METADATA_UPDATED:
      case HMSNotificationTypes.ROLE_UPDATED:
        break;
      case HMSNotificationTypes.TRACK_DEGRADED:
        logger.log(`track - ${notification.data} degraded due to poor network`);
        {
          const trackId =
            typeof notification.data === "string"
              ? notification.data
              : notification.data?.id;
          if (trackId) {
            globalStores?.curTalkingParty?.setTrackDegraded(trackId, true);
          }
        }
        break;
      case HMSNotificationTypes.TRACK_RESTORED:
        logger.log(`track - ${notification.data} recovered`);
        {
          const trackId =
            typeof notification.data === "string"
              ? notification.data
              : notification.data?.id;
          if (trackId) {
            globalStores?.curTalkingParty?.setTrackDegraded(trackId, false);
          }
        }
        break;
      case HMSNotificationTypes.TRACK_ADDED:
        // On iOS, newly added remote audio tracks may not play if the
        // AudioContext was suspended.  Re-unlock proactively.
        if (isIOS() && notification.data?.type === "audio") {
          resumeSharedAudioContext();
          hmsActions.unblockAudio();
        }
        break;
      case HMSNotificationTypes.TRACK_REMOVED:
        break;
      case HMSNotificationTypes.TRACK_MUTED:
        break;
      case HMSNotificationTypes.TRACK_UNMUTED:
        // When a remote audio track is unmuted, ensure we can hear it
        if (isIOS() && notification.data?.type === "audio") {
          resumeSharedAudioContext();
        }
        break;
      case HMSNotificationTypes.ROOM_ENDED:
        logger.log(`room ended, reason - ${notification.data.reason}`);
        break;
      case HMSNotificationTypes.REMOVED_FROM_ROOM:
        logger.log(`removed from room, reason - ${notification.data.reason}`);
        break;
      case HMSNotificationTypes.DEVICE_CHANGE_UPDATE:
        logger.log(`device changed - ${notification.data}`);
        break;
      default:
        break;
    }
  }, [notification, hmsActions]);
}
